import React, { useState } from 'react';
import { Button, Card } from './ui';

export interface SystemCheckinData {
    weather?: 'thunderstorm' | 'foggy' | 'overcast' | 'clear' | 'heat';
    words?: string[]; // 3 selected words
    battery?: number; // 0-100
    diagnosticState?: 'Green' | 'Yellow' | 'Orange' | 'Red';
    actionCategory?: 'A' | 'B' | 'C';
    legacyValue?: number;
    compositeLabel?: string;
}

interface SystemCheckFlowProps {
    onComplete: (data: SystemCheckinData) => void;
    onCancel: () => void;
}

const WEATHER_OPTIONS = [
    { id: 'thunderstorm', emoji: '⛈️', label: 'Thunder', penalty: 30 },
    { id: 'foggy', emoji: '🌫️', label: 'Foggy', penalty: 20 },
    { id: 'overcast', emoji: '☁️', label: 'Heavy', penalty: 10 },
    { id: 'heat', emoji: '🔥', label: 'Stressed', penalty: 25 },
    { id: 'clear', emoji: '☀️', label: 'Clear', penalty: 0 },
];

const WORD_CLOUD = [
    'Wired', 'Numb', 'Proud', 'Drained', 'Cluttered', 'Focused', 
    'Angry', 'Invisible', 'Capable', 'Trapped', 'Supported', 'Exhausted',
    'Panicked', 'Present', 'Resentful'
];

export const SystemCheckFlow: React.FC<SystemCheckFlowProps> = ({ onComplete, onCancel }) => {
    const [weather, setWeather] = useState<SystemCheckinData['weather']>('clear');
    const [selectedWords, setSelectedWords] = useState<string[]>([]);
    const [battery, setBattery] = useState<number>(50);
    const [isAnalyzing, setIsAnalyzing] = useState(false);

    const handleWordSelect = (word: string) => {
        if (selectedWords.includes(word)) {
            setSelectedWords(prev => prev.filter(w => w !== word));
        } else if (selectedWords.length < 3) {
            setSelectedWords(prev => [...prev, word]);
        }
    };

    const handleComplete = () => {
        setIsAnalyzing(true);
        
        // Calculate concise diagnostic
        const weatherOpt = WEATHER_OPTIONS.find(w => w.id === weather);
        let penalty = weatherOpt ? weatherOpt.penalty : 0;
        
        // Add extra penalty/bonus for severe words
        if (selectedWords.includes('Wired') || selectedWords.includes('Angry') || selectedWords.includes('Panicked')) penalty += 15;
        if (selectedWords.includes('Focused') || selectedWords.includes('Supported') || selectedWords.includes('Capable')) penalty -= 15;
        
        let score = Math.max(0, Math.min(100, battery - penalty));

        let diagState: SystemCheckinData['diagnosticState'] = 'Orange';
        let category: SystemCheckinData['actionCategory'] = 'B'; 

        if (score >= 80) diagState = 'Green';
        else if (score >= 60) diagState = 'Yellow';
        else if (score >= 35) diagState = 'Orange';
        else diagState = 'Red';

        if (weather === 'thunderstorm' || weather === 'heat' || selectedWords.includes('Wired') || selectedWords.includes('Angry')) category = 'A';
        else if (weather === 'foggy' || weather === 'overcast' || selectedWords.includes('Numb') || selectedWords.includes('Drained')) category = 'C';

        let legacy = 0;
        if (diagState === 'Green') legacy = 4;
        if (diagState === 'Yellow') legacy = 1;
        if (diagState === 'Orange') legacy = -2;
        if (diagState === 'Red') legacy = -4;

        setTimeout(() => {
            onComplete({
                weather,
                words: selectedWords,
                battery,
                diagnosticState: diagState,
                actionCategory: category,
                legacyValue: legacy,
                compositeLabel: `${diagState} State (${category})`
            });
        }, 600);
    };

    if (isAnalyzing) {
        return (
            <div className="bg-md-surface-container p-6 md:p-8 rounded-[32px] w-full max-w-lg mx-auto shadow-lg border-0 text-center animate-in fade-in">
                <div className="flex flex-col items-center justify-center py-12">
                     <div className="text-6xl mb-4 animate-bounce">⚙️</div>
                     <h3 className="text-2xl font-black text-md-on-surface font-heading tracking-tight">Logging check-in...</h3>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-md-surface-container p-5 md:p-6 rounded-[32px] w-full max-w-lg mx-auto shadow-2xl border-0 relative max-h-[90vh] overflow-y-auto custom-scrollbar animate-in fade-in">
            <button onClick={onCancel} className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-md-surface-variant hover:bg-slate-300 text-md-on-surface-variant transition-colors z-20 font-bold">
                ✕
            </button>

            <span className="text-md-primary font-bold uppercase tracking-widest text-[10px] mb-1 block">Rapid Check-in</span>
            <h3 className="text-xl font-black text-md-on-surface font-heading mb-4 tracking-tight">How's the engine running?</h3>
            
            <div className="mb-4">
                <p className="font-bold text-xs text-md-on-surface-variant uppercase tracking-widest mb-2">1. Weather in your head</p>
                <div className="flex flex-wrap gap-2">
                    {WEATHER_OPTIONS.map(opt => (
                        <button 
                            key={opt.id}
                            onClick={() => setWeather(opt.id as any)}
                            className={`flex-1 min-w-[30%] py-2 px-1 rounded-[12px] border-2 transition-all flex flex-col items-center gap-1 hover:scale-[1.02] active:scale-95 ${weather === opt.id ? 'bg-md-primary/10 border-md-primary text-md-primary shadow-sm' : 'bg-md-surface border-md-outline-variant/30 text-slate-500'}`}
                        >
                            <span className="text-xl">{opt.emoji}</span>
                            <span className="text-[10px] uppercase font-bold">{opt.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="mb-4 bg-md-surface-container-low p-3 rounded-[20px] border border-md-outline-variant/30">
                <div className="flex items-center justify-between mb-2">
                    <p className="font-bold text-xs text-md-on-surface-variant uppercase tracking-widest">2. Pick 3 words</p>
                    <span className="text-[10px] font-bold text-md-primary">{selectedWords.length}/3 selected</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {WORD_CLOUD.map(word => {
                        const isSelected = selectedWords.includes(word);
                        const disabled = !isSelected && selectedWords.length >= 3;
                        return (
                            <button
                                key={word}
                                onClick={() => handleWordSelect(word)}
                                disabled={disabled}
                                className={`px-2 py-1 rounded-full font-bold text-[10px] md:text-xs transition-all ${isSelected ? 'bg-md-primary text-md-on-primary shadow-sm scale-105 border border-md-primary' : disabled ? 'bg-slate-50 text-slate-300 border border-slate-100 cursor-not-allowed' : 'bg-white text-md-on-surface hover:bg-md-surface-variant border border-md-outline-variant shadow-sm'}`}
                            >
                                {word}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="mb-6">
                <p className="font-bold text-xs text-md-on-surface-variant uppercase tracking-widest mb-2">3. Physical Battery</p>
                <div className="py-5 px-4 bg-md-surface-container-low rounded-[20px] border border-md-outline-variant/30 relative">
                     <div className="absolute top-3 left-3 text-xl">{battery < 30 ? '🪫' : battery > 80 ? '🔋' : '🔌'}</div>
                     <h4 className="text-center font-black text-3xl text-md-on-surface mb-4 font-mono">{battery}%</h4>
                     <input 
                        type="range" 
                        min="0" max="100" 
                        value={battery} 
                        onChange={(e) => setBattery(Number(e.target.value))}
                        className="w-full h-4 bg-md-surface-container-highest rounded-full appearance-none cursor-pointer accent-md-primary shadow-inner"
                    />
                </div>
            </div>

            <Button 
                onClick={handleComplete} 
                className="w-full h-12 font-bold bg-md-primary hover:bg-md-primary/90 text-md-on-primary rounded-[16px] text-base shadow-md transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100"
                disabled={selectedWords.length !== 3}
            >
                {selectedWords.length === 3 ? 'Save Check-in' : `Pick ${3 - selectedWords.length} more words`}
            </Button>
        </div>
    );
};
